import React, { useState, useEffect } from "react";
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiEye,
  HiEyeSlash,
  HiXMark,
  HiCheck,
} from "react-icons/hi2";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import SearchableSelect from "../../components/common/SearchableSelect";

interface WellnessQuestion {
  id: string;
  question: string;
  answers: string[];
  category: string;
  title?: string;
  video?: string;
  active: boolean;
  createdAt?: any;
  updatedAt?: any;
}

interface QuestionFormData {
  question: string;
  answers: string[];
  category: string;
  title: string;
  video: string;
  active: boolean;
}

const WellnessQuestionManagement: React.FC = () => {
  const [questions, setQuestions] = useState<WellnessQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] =
    useState<WellnessQuestion | null>(null);
  const [formData, setFormData] = useState<QuestionFormData>({
    question: "",
    answers: ["", "", "", "", "", "", ""],
    category: "mood",
    title: "",
    video: "",
    active: true,
  });

  const categories = [
    "mood",
    "recovery",
    "sleep",
    "stress",
    "health",
    "physical",
  ];

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("Fetching wellness questions from database...");
      const questionsRef = collection(db, "wellnessQuestions");

      // Try simple query first to see if there are any documents
      const simpleSnapshot = await getDocs(questionsRef);
      console.log(
        "Total documents in wellnessQuestions collection:",
        simpleSnapshot.docs.length
      );

      if (simpleSnapshot.empty) {
        console.log("No documents found in wellnessQuestions collection");
        setQuestions([]);
        setError(
          "No wellness questions found in the database. They may need to be migrated or created."
        );
        return;
      }

      const questionsData = simpleSnapshot.docs.map((doc) => {
        const data = doc.data();
        console.log(`Question ${doc.id}:`, data);
        return {
          id: doc.id,
          ...data,
          // Ensure active field exists (default to true if missing)
          active: data.active !== undefined ? data.active : true,
          // Ensure answers is an array
          answers: Array.isArray(data.answers) ? data.answers : [],
          // Ensure category exists
          category: data.category || "unknown",
        };
      }) as WellnessQuestion[];

      // Sort by category on the client side
      questionsData.sort((a, b) => a.category.localeCompare(b.category));

      console.log("Successfully processed questions:", questionsData.length);
      setQuestions(questionsData);
      setError(null);
    } catch (error: any) {
      console.error("Error fetching questions:", error);
      setError(`Failed to fetch questions: ${error.message}`);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const questionData = {
        ...formData,
        answers: formData.answers.filter((answer) => answer.trim() !== ""),
        createdAt: editingQuestion
          ? editingQuestion.createdAt
          : Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      if (editingQuestion) {
        // Update existing question
        await updateDoc(
          doc(db, "wellnessQuestions", editingQuestion.id),
          questionData
        );
      } else {
        // Create new question
        await addDoc(collection(db, "wellnessQuestions"), questionData);
      }

      // Reset form and refresh data
      setFormData({
        question: "",
        answers: ["", "", "", "", "", "", ""],
        category: "mood",
        title: "",
        video: "",
        active: true,
      });
      setShowForm(false);
      setEditingQuestion(null);
      await fetchQuestions();
    } catch (error) {
      console.error("Error saving question:", error);
    }
  };

  const handleEdit = (question: WellnessQuestion) => {
    setEditingQuestion(question);
    setFormData({
      question: question.question,
      answers: [
        ...question.answers,
        ...Array(7 - question.answers.length).fill(""),
      ],
      category: question.category,
      title: question.title || "",
      video: question.video || "",
      active: question.active,
    });
    setShowForm(true);
  };

  const handleDelete = async (questionId: string) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        await deleteDoc(doc(db, "wellnessQuestions", questionId));
        await fetchQuestions();
      } catch (error) {
        console.error("Error deleting question:", error);
      }
    }
  };

  const toggleActive = async (question: WellnessQuestion) => {
    try {
      await updateDoc(doc(db, "wellnessQuestions", question.id), {
        active: !question.active,
        updatedAt: Timestamp.now(),
      });
      await fetchQuestions();
    } catch (error) {
      console.error("Error toggling question status:", error);
    }
  };

  const handleAnswerChange = (index: number, value: string) => {
    const newAnswers = [...formData.answers];
    newAnswers[index] = value;
    setFormData({ ...formData, answers: newAnswers });
  };

  const cancelEdit = () => {
    setShowForm(false);
    setEditingQuestion(null);
    setFormData({
      question: "",
      answers: ["", "", "", "", "", "", ""],
      category: "mood",
      title: "",
      video: "",
      active: true,
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold">
          Wellness Questions Management
        </h1>
        <button
          onClick={() => setShowForm(true)}
          className=" -primary flex items-center gap-2"
        >
          <HiPlus className="w-5 h-5" />
          Add New Question
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-error mb-6">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <div>
              <h3 className="font-medium">Error loading questions</h3>
              <p className="text-sm">{error}</p>
            </div>
          </div>
          <button onClick={fetchQuestions} className=" -sm -outline">
            Retry
          </button>
        </div>
      )}

      {/* Debug Info */}
      {!loading && !error && (
        <div className="mb-4 p-4 bg-blue-500/10 rounded-lg">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            📊 Found {questions.length} wellness questions total
          </p>
        </div>
      )}

      {/* Question Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="bg-base-100 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    {editingQuestion ? "Edit Question" : "Add New Question"}
                  </h2>
                  <button
                    onClick={cancelEdit}
                    className="p-2 hover:bg-base-200 rounded-full"
                  >
                    <HiXMark className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Category */}
                  <div>
                    <SearchableSelect
                      options={categories.map((category) => ({
                        value: category,
                        label: category.charAt(0).toUpperCase() + category.slice(1),
                      }))}
                      value={formData.category}
                      onChange={(value) =>
                        setFormData({ ...formData, category: value })
                      }
                      placeholder="Search categories..."
                      allOptionLabel="Select Category"
                      emptyValue=""
                      label="Category"
                      inputClassName="bg-base-200"
                    />
                  </div>

                  {/* Question */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Question
                    </label>
                    <textarea
                      value={formData.question}
                      onChange={(e) =>
                        setFormData({ ...formData, question: e.target.value })
                      }
                      className="textarea textarea-bordered w-full"
                      rows={3}
                      required
                    />
                  </div>

                  {/* Title (Optional) */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Title (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      className="input input-bordered w-full"
                    />
                  </div>

                  {/* Video URL (Optional) */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Video URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={formData.video}
                      onChange={(e) =>
                        setFormData({ ...formData, video: e.target.value })
                      }
                      className="input input-bordered w-full"
                      placeholder="https://..."
                    />
                  </div>

                  {/* Answers */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Answers (1-7 Scale)
                    </label>
                    <div className="space-y-2">
                      {formData.answers.map((answer, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <span className="text-sm font-medium w-8">
                            {index + 1}:
                          </span>
                          <input
                            type="text"
                            value={answer}
                            onChange={(e) =>
                              handleAnswerChange(index, e.target.value)
                            }
                            className="input input-bordered flex-1"
                            placeholder={`Answer for scale point ${index + 1}`}
                            required={index < 3} // First 3 answers are required
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Active Status */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) =>
                        setFormData({ ...formData, active: e.target.checked })
                      }
                      className="checkbox checkbox-primary"
                    />
                    <span className="text-sm font-medium">Active</span>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-between gap-2 pt-4 border-t">
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className=" -ghost"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-neutral text-white py-3 px-6 flex items-center gap-2 rounded-md"
                    >
                      <HiCheck className="w-4 h-4" />
                      {editingQuestion ? "Update" : "Create"} Question
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Questions List */}
      <div className="space-y-4">
        {categories.map((category) => {
          const categoryQuestions = questions.filter(
            (q) => q.category === category
          );

          if (categoryQuestions.length === 0) return null;

          return (
            <div
              key={category}
              className="bg-base-100 rounded-lg shadow-sm border p-6"
            >
              <h2 className="text-xl font-semibold mb-4 capitalize">
                {category} Questions ({categoryQuestions.length})
              </h2>

              <div className="space-y-3">
                {categoryQuestions.map((question) => (
                  <div
                    key={question.id}
                    className={`border rounded-lg p-4 ${
                      question.active
                        ? "border-base-300"
                        : "border-red-500/30 bg-red-500/10"
                    }`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        {question.title && (
                          <h3 className="font-medium text-primary mb-1">
                            {question.title}
                          </h3>
                        )}
                        <p className="text-base-content mb-2">
                          {question.question}
                        </p>
                        <div className="text-sm text-base-content/70">
                          <p>
                            <strong>Answers:</strong> {question.answers.length}{" "}
                            scale points
                          </p>
                          {question.video && (
                            <p>
                              <strong>Video:</strong> Included
                            </p>
                          )}
                          <p>
                            <strong>Status:</strong>{" "}
                            {question.active ? "Active" : "Inactive"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleActive(question)}
                          className={` -sm ${
                            question.active ? "-warning" : "-success"
                          }`}
                          title={question.active ? "Deactivate" : "Activate"}
                        >
                          {question.active ? (
                            <HiEyeSlash className="w-4 h-4" />
                          ) : (
                            <HiEye className="w-4 h-4" />
                          )}
                        </button>

                        <button
                          onClick={() => handleEdit(question)}
                          className=" -sm -primary"
                          title="Edit"
                        >
                          <HiPencil className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => handleDelete(question.id)}
                          className=" -sm -error"
                          title="Delete"
                        >
                          <HiTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {questions.length === 0 && (
        <div className="text-center py-12">
          <p className="text-base-content/60 text-lg">No wellness questions found.</p>
          <button onClick={() => setShowForm(true)} className=" -primary mt-4">
            Create Your First Question
          </button>
        </div>
      )}
    </div>
  );
};

export default WellnessQuestionManagement;
